import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDate, addDaysIso } from "@/lib/dates";
import { DateInput } from "@/components/DateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useT } from "@/i18n";
import { X } from "lucide-react";
import { ClientAutocomplete } from "@/components/ClientAutocomplete";

const MATTER_TYPES = [
  "Civil litigation", "Arbitration", "Maritime / Shipping", "Transport / CMR",
  "Insolvency", "Criminal", "Administrative", "M&A / Corporate", "Advisory", "Other",
];

const EMPTY_MATTER = {
  name: "", client: "", client_id: "", opposing_party: "", internal_ref: "", court_authority: "",
  court_case_number: "", matter_type: "Civil litigation", matter_type_other: "",
  status: "Active", responsible_lawyer: "", notes: "", dropbox_link: "",
  billing_type: "Hourly", default_hourly_rate: 0,
  everything_query: "", obsidian_vault: "", obsidian_note: "", zotero_uri: "", outlook_ref: "",
};

export function MatterDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_MATTER);
  const { t } = useT();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY_MATTER, ...initial } : EMPTY_MATTER);
      setError("");
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    const payload = {
      ...form,
      default_hourly_rate: form.default_hourly_rate === "" ? 0 : Number(form.default_hourly_rate) || 0,
    };
    try {
      const r = isEdit
        ? await api.put(`/matters/${initial.id}`, payload)
        : await api.post("/matters", payload);
      onSaved(r.data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["name", "form.matter_name", true, "matter-form-name"],
    ["client", "form.client", true, "matter-form-client"],
    ["opposing_party", "form.opposing_party", false, "matter-form-opposing-party"],
    ["internal_ref", "form.internal_ref", false, "matter-form-internal-ref"],
    ["court_authority", "form.court_authority", false, "matter-form-court-authority"],
    ["court_case_number", "form.case_number", false, "matter-form-case-number"],
    ["responsible_lawyer", "form.responsible_lawyer", false, "matter-form-lawyer"],
    ["dropbox_link", "form.dropbox_link", false, "matter-form-dropbox"],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="matter-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">
            {t(isEdit ? "form.matter_edit" : "form.matter_new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="matter-form">
          {error && (
            <div data-testid="matter-form-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(([key, label, required, testid]) => (
              <div key={key} className={key === "name" ? "sm:col-span-2" : ""}>
                <Label htmlFor={`mf-${key}`}>
                  {t(label)} {required && <span className="text-red-600">*</span>}
                </Label>
                {key === "client" ? (
                  <ClientAutocomplete
                    value={form.client}
                    clientId={form.client_id}
                    required={required}
                    testid={testid}
                    onChange={({ client, client_id }) => setForm((f) => ({ ...f, client, client_id }))}
                  />
                ) : (
                  <Input
                    id={`mf-${key}`}
                    data-testid={testid}
                    required={required}
                    value={form[key]}
                    onChange={set(key)}
                    className="mt-1.5"
                  />
                )}
              </div>
            ))}
            <div>
              <Label>{t("common.status")}</Label>
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="matter-form-status" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Active", "Pending", "On hold", "Closed", "Archived"].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`matter-status-option-${s.toLowerCase().replace(" ", "-")}`}>{t(`val.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("form.matter_type")}</Label>
              <Select value={form.matter_type} onValueChange={set("matter_type")}>
                <SelectTrigger data-testid="matter-form-type" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MATTER_TYPES.map((mt) => (
                    <SelectItem
                      key={mt}
                      value={mt}
                      data-testid={`matter-type-option-${mt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {t(`val.${mt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {form.matter_type === "Other" && (
              <div className="sm:col-span-2">
                <Label htmlFor="mf-type-other">{t("form.matter_type_other")}</Label>
                <Input
                  id="mf-type-other"
                  data-testid="matter-form-type-other"
                  value={form.matter_type_other}
                  onChange={set("matter_type_other")}
                  placeholder={t("form.matter_type_other_ph")}
                  className="mt-1.5"
                />
              </div>
            )}
            <div>
              <Label>{t("form.billing_type")}</Label>
              <Select value={form.billing_type} onValueChange={set("billing_type")}>
                <SelectTrigger data-testid="matter-form-billing-type" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Hourly", "Fixed Fee", "Non-billable"].map((b) => (
                    <SelectItem
                      key={b}
                      value={b}
                      data-testid={`billing-type-option-${b.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {t(`val.${b}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="mf-rate">{t("form.default_rate")}</Label>
              <Input
                id="mf-rate"
                data-testid="matter-form-rate"
                type="number"
                min="0"
                step="0.01"
                value={form.default_hourly_rate}
                onChange={set("default_hourly_rate")}
                className="mt-1.5"
              />
            </div>
            <div className="sm:col-span-2 border-t border-slate-100 pt-4" data-testid="external-links-fieldset">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{t("form.external_links")}</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label htmlFor="mf-everything">{t("form.everything_query")}</Label>
                  <Input
                    id="mf-everything"
                    data-testid="matter-form-everything-query"
                    value={form.everything_query}
                    onChange={set("everything_query")}
                    placeholder="Client name RPL-2026-014"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="mf-obs-vault">{t("form.obsidian_vault")}</Label>
                  <Input
                    id="mf-obs-vault"
                    data-testid="matter-form-obsidian-vault"
                    value={form.obsidian_vault}
                    onChange={set("obsidian_vault")}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="mf-obs-note">{t("form.obsidian_note")}</Label>
                  <Input
                    id="mf-obs-note"
                    data-testid="matter-form-obsidian-note"
                    value={form.obsidian_note}
                    onChange={set("obsidian_note")}
                    placeholder="obsidian://open?vault=…&file=…"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="mf-zotero">{t("form.zotero_uri")}</Label>
                  <Input
                    id="mf-zotero"
                    data-testid="matter-form-zotero-uri"
                    value={form.zotero_uri}
                    onChange={set("zotero_uri")}
                    placeholder="zotero://…"
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label htmlFor="mf-outlook">{t("form.outlook_ref")}</Label>
                  <Input
                    id="mf-outlook"
                    data-testid="matter-form-outlook-ref"
                    value={form.outlook_ref}
                    onChange={set("outlook_ref")}
                    placeholder="https://outlook.office.com/…"
                    className="mt-1.5"
                  />
                </div>
              </div>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="mf-notes">{t("form.short_notes")}</Label>
              <Textarea
                id="mf-notes"
                data-testid="matter-form-notes"
                value={form.notes}
                onChange={set("notes")}
                rows={3}
                className="mt-1.5"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="matter-form-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" data-testid="matter-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? t("common.saving") : isEdit ? t("form.save_changes") : t("form.create_matter")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ReminderDatesInput({ value, onChange }) {
  const { t } = useT();
  const [d, setD] = useState("");
  return (
    <div>
      <div className="flex gap-2">
        <DateInput testid="deadline-form-reminder-input" value={d} onChange={setD} />
        <Button
          type="button"
          variant="outline"
          data-testid="deadline-form-add-reminder"
          onClick={() => {
            if (d && !value.includes(d)) onChange([...value, d].sort());
            setD("");
          }}
        >
          {t("common.add")}
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2" data-testid="deadline-form-reminder-chips">
          {value.map((r) => (
            <span
              key={r}
              data-testid={`reminder-chip-${r}`}
              className="inline-flex items-center gap-1 text-xs bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1"
            >
              {formatDate(r)}
              <button
                type="button"
                data-testid={`reminder-chip-remove-${r}`}
                onClick={() => onChange(value.filter((x) => x !== r))}
                className="text-slate-400 hover:text-red-600"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

const EMPTY_DEADLINE = {
  description: "", due_date: "", deadline_type: "Procedural deadline",
  start_time: "", end_time: "",
  reminder_dates: [], status: "Open", priority: "Medium", notes: "",
};

const DEADLINE_TYPES = ["Procedural deadline", "Court hearing", "Client deadline", "Internal deadline", "Other"];
const QUICK_REMINDER_OFFSETS = [14, 7, 3, 1];

export function DeadlineDialog({ open, onOpenChange, matterId, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_DEADLINE);
  const { t } = useT();
  const [quickReminders, setQuickReminders] = useState([]);
  const [customReminders, setCustomReminders] = useState([]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial && initial.id);

  useEffect(() => {
    if (open) {
      const base = initial ? { ...EMPTY_DEADLINE, ...initial } : EMPTY_DEADLINE;
      setForm(base);
      const quick = [];
      const custom = [];
      for (const r of base.reminder_dates || []) {
        const off = QUICK_REMINDER_OFFSETS.find((o) => base.due_date && addDaysIso(base.due_date, -o) === r);
        if (off !== undefined) quick.push(off);
        else custom.push(r);
      }
      setQuickReminders(quick);
      setCustomReminders(custom);
      setError("");
    }
  }, [open, initial]);

  const toggleQuick = (o) =>
    setQuickReminders((cur) => (cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    if (!form.due_date) {
      setError(t("form.invalid_due_date"));
      setSaving(false);
      return;
    }
    const reminder_dates = [...new Set([
      ...quickReminders.map((o) => addDaysIso(form.due_date, -o)),
      ...customReminders,
    ])].sort();
    const payload = { ...form, reminder_dates };
    try {
      if (isEdit) await api.put(`/deadlines/${initial.id}`, payload);
      else await api.post(`/matters/${matterId}/deadlines`, payload);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="deadline-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t(isEdit ? "form.deadline_edit" : "form.deadline_new")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="deadline-form">
          {error && (
            <div data-testid="deadline-form-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="df-desc">{t("common.description")} <span className="text-red-600">*</span></Label>
            <Input
              id="df-desc"
              data-testid="deadline-form-description"
              required
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="df-date">{t("common.due_date")} <span className="text-red-600">*</span></Label>
              <div className="mt-1.5">
                <DateInput
                  id="df-date"
                  testid="deadline-form-due-date"
                  value={form.due_date}
                  onChange={(v) => setForm((f) => ({ ...f, due_date: v }))}
                />
              </div>
            </div>
            <div>
              <Label>{t("form.deadline_type")}</Label>
              <Select value={form.deadline_type} onValueChange={(v) => setForm((f) => ({ ...f, deadline_type: v }))}>
                <SelectTrigger data-testid="deadline-form-type" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DEADLINE_TYPES.map((dt) => (
                    <SelectItem
                      key={dt}
                      value={dt}
                      data-testid={`deadline-type-option-${dt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    >
                      {t(`val.${dt}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.deadline_type === "Court hearing" && (
            <div className="grid grid-cols-2 gap-4" data-testid="deadline-hearing-times">
              <div>
                <Label htmlFor="df-start">{t("form.start_time")}</Label>
                <Input
                  id="df-start"
                  type="time"
                  data-testid="deadline-form-start-time"
                  value={form.start_time || ""}
                  onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label htmlFor="df-end">{t("form.end_time")}</Label>
                <Input
                  id="df-end"
                  type="time"
                  data-testid="deadline-form-end-time"
                  value={form.end_time || ""}
                  onChange={(e) => setForm((f) => ({ ...f, end_time: e.target.value }))}
                  className="mt-1.5"
                />
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>{t("common.priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="deadline-form-priority" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["High", "Medium", "Low"].map((p) => (
                    <SelectItem key={p} value={p} data-testid={`deadline-priority-option-${p.toLowerCase()}`}>{t(`val.${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("common.status")}</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="deadline-form-status" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Open", "Completed"].map((s) => (
                    <SelectItem key={s} value={s} data-testid={`deadline-status-option-${s.toLowerCase()}`}>{t(`val.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("form.quick_reminders")}</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2" data-testid="deadline-form-quick-reminders">
              {QUICK_REMINDER_OFFSETS.map((o) => {
                const checked = quickReminders.includes(o);
                const computed = form.due_date ? addDaysIso(form.due_date, -o) : null;
                return (
                  <label
                    key={o}
                    data-testid={`quick-reminder-${o}`}
                    className={`flex items-center gap-2 text-xs border rounded-md px-3 py-2 cursor-pointer transition-colors ${
                      checked ? "border-slate-800 bg-slate-50 font-semibold" : "border-slate-200"
                    } ${!form.due_date ? "opacity-50 pointer-events-none" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleQuick(o)}
                      className="accent-slate-800"
                    />
                    <span>
                      {o === 1 ? t("form.day_before") : t("form.days_before", { n: o })}{computed ? ` · ${formatDate(computed)}` : ""}
                    </span>
                  </label>
                );
              })}
            </div>
            {!form.due_date && (
              <div className="text-[11px] text-slate-400 mt-1">{t("form.set_due_first")}</div>
            )}
          </div>
          <div>
            <Label>{t("form.custom_reminders")}</Label>
            <div className="mt-1.5">
              <ReminderDatesInput value={customReminders} onChange={setCustomReminders} />
            </div>
          </div>
          <div>
            <Label htmlFor="df-notes">{t("common.notes")}</Label>
            <Textarea
              id="df-notes"
              data-testid="deadline-form-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="mt-1.5"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="deadline-form-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" data-testid="deadline-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? t("common.saving") : isEdit ? t("form.save_changes") : t("form.add_deadline")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const EMPTY_TASK = { description: "", due_date: "", status: "Open", priority: "Medium", notes: "" };

export function TaskDialog({ open, onOpenChange, matterId, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY_TASK);
  const { t } = useT();
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(initial && initial.id);

  useEffect(() => {
    if (open) {
      setForm(initial ? { ...EMPTY_TASK, ...initial } : EMPTY_TASK);
      setError("");
    }
  }, [open, initial]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    if (form.due_date === null) {
      setError(t("form.invalid_due_date"));
      setSaving(false);
      return;
    }
    try {
      if (isEdit) await api.put(`/tasks/${initial.id}`, form);
      else await api.post(`/matters/${matterId}/tasks`, form);
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl">{t(isEdit ? "form.task_edit" : "form.task_new")}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="task-form">
          {error && (
            <div data-testid="task-form-error" className="text-sm text-red-800 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <Label htmlFor="tf-desc">{t("common.description")} <span className="text-red-600">*</span></Label>
            <Input
              id="tf-desc"
              data-testid="task-form-description"
              required
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              className="mt-1.5"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="tf-date">{t("common.due_date")}</Label>
              <div className="mt-1.5">
                <DateInput
                  id="tf-date"
                  testid="task-form-due-date"
                  value={form.due_date}
                  onChange={(v) => setForm((f) => ({ ...f, due_date: v }))}
                />
              </div>
            </div>
            <div>
              <Label>{t("common.priority")}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                <SelectTrigger data-testid="task-form-priority" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["High", "Medium", "Low"].map((p) => (
                    <SelectItem key={p} value={p} data-testid={`task-priority-option-${p.toLowerCase()}`}>{t(`val.${p}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>{t("common.status")}</Label>
            <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
              <SelectTrigger data-testid="task-form-status" className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {["Open", "Completed"].map((s) => (
                  <SelectItem key={s} value={s} data-testid={`task-status-option-${s.toLowerCase()}`}>{t(`val.${s}`)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="tf-notes">{t("common.notes")}</Label>
            <Textarea
              id="tf-notes"
              data-testid="task-form-notes"
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={2}
              className="mt-1.5"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" data-testid="task-form-cancel" onClick={() => onOpenChange(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" data-testid="task-form-submit" disabled={saving} className="bg-slate-900 hover:bg-slate-800">
              {saving ? t("common.saving") : isEdit ? t("form.save_changes") : t("form.add_task")}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
